/**
 * One line of the Forecast Details "calculated vs override" table: what the
 * engine set, what a buyer pinned over it, and what applies — side by side, the
 * way GAINS shows them — plus the save path its inline editor calls.
 *
 * The table knows nothing about any particular override kind. Every row is
 * produced by a source hook (`inlineOverrideSources.ts`) that reads its own
 * existing API and describes itself with this shape, so a new override kind
 * (a pack/carton rounding override, say) slots in by adding one more source
 * rather than by editing the table.
 */

import type { InventoryPolicyOverrideRow } from '@erp/rpc';
import type { OverrideWindowGrain } from '../../../replenishment/components/overrideWindow';
import type { OverrideWindowPayload } from '../../../replenishment/components/useOverrideWindowState';

/** How a row's number is read, typed, and printed. */
export type InlineOverrideUnit = 'units' | 'days' | 'percent';

/** The override row currently governing a field: its audit trail. */
export interface GoverningOverride {
  id: string;
  /** The value it pins, in the row's display unit (percent for a factor). */
  value: number;
  startsAt: string;
  endsAt: string | null;
  reviewAt: string | null;
  needsReview: boolean;
  reason: string | null;
  createdByUserName: string | null;
  createdAt: string;
}

export interface InlineOverrideSave {
  /** In the row's display unit. */
  value: number;
  window: OverrideWindowPayload;
  /**
   * The window starts in the future. The save then writes a new, scheduled row
   * and never revokes the governing one, which keeps applying until the new row
   * starts and wins as the newer writer.
   */
  startsLater: boolean;
}

export interface InlineOverrideField {
  /** Stable, unique across every source. */
  id: string;
  /** Which override kind this row belongs to — groups rows and labels the audit trail. */
  kind: string;
  label: string;
  unit: InlineOverrideUnit;
  /** The engine's own number, overrides ignored; null when it has none. */
  calculated: number | null;
  /** One short line on where `calculated` came from. */
  calculatedHint?: string;
  /** What consumers use right now. */
  effective: number | null;
  /** The active override setting this field, or null when the engine's number stands. */
  override: GoverningOverride | null;
  /** Day-grain for policy and lead-time windows, month-grain for growth factors. */
  grain: OverrideWindowGrain;
  /** Null when the field can be pinned right now; otherwise why not. */
  lockedReason: string | null;
  /** Null when the typed value is acceptable; otherwise what is wrong. */
  validate: (value: number) => string | null;
  /**
   * True when a save revokes the governing row and replaces it; false when it
   * writes a new row (none governs, or a newer row shadows a multi-field one).
   * A save that starts later always writes a new row.
   */
  replaces: boolean;
  /** Creates an override, or replaces the governing one. Rejects on failure. */
  save: (input: InlineOverrideSave) => Promise<unknown>;
  pending: boolean;
}

/** The policy fields a buyer can pin inline: the numeric ones. */
export const INLINE_POLICY_FIELDS = [
  'minQuantity',
  'maxQuantity',
  'safetyStock',
  'minFloor',
  'hardMin',
  'hardMax',
  'orderMultiple',
] as const;

export type InlinePolicyField = (typeof INLINE_POLICY_FIELDS)[number];

const POLICY_FIELDS: readonly (keyof InventoryPolicyOverrideRow)[] = [
  'minQuantity',
  'maxQuantity',
  'safetyStock',
  'minFloor',
  'hardMin',
  'hardMax',
  'orderMultiple',
  'autoTransferEligible',
  'autoBuyEligible',
];

/**
 * The active override the server reads `field` from: per field, the newest
 * active row that sets it (created_at DESC, id DESC) — the same
 * last-writer-wins rule `effectivePolicy.ts` resolves with. A row that sets
 * nothing for `field` never governs it, however new.
 */
export function governingPolicyOverride(
  overrides: readonly InventoryPolicyOverrideRow[],
  field: InlinePolicyField,
): InventoryPolicyOverrideRow | null {
  let best: InventoryPolicyOverrideRow | null = null;
  for (const o of overrides) {
    if (!o.isActive || o[field] === null) continue;
    if (
      !best ||
      o.createdAt > best.createdAt ||
      (o.createdAt === best.createdAt && o.id > best.id)
    ) {
      best = o;
    }
  }
  return best;
}

/**
 * Whether `row` pins `field` and nothing else. Only such a row can be edited
 * in place (revoke + replace): replacing a row that also pins, say, max would
 * drop the max pin with it, so an inline edit of one field of a multi-field row
 * writes a newer single-field row that shadows it instead.
 */
export function pinsOnly(
  row: InventoryPolicyOverrideRow,
  field: InlinePolicyField,
): boolean {
  return POLICY_FIELDS.every((f) => (f === field) === (row[f] !== null));
}

/**
 * The buyer's typed text as a number in the row's unit, or null while it is
 * empty or not a number. Days are whole; quantities and percents may carry a
 * decimal.
 */
export function parseInlineValue(
  raw: string,
  unit: InlineOverrideUnit,
): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  if (unit === 'days' && !Number.isInteger(n)) return null;
  return n;
}

/** A value as the table prints it; null (the engine has no number) is a dash. */
export function formatInlineValue(
  value: number | null,
  unit: InlineOverrideUnit,
): string {
  if (value === null) return '—';
  switch (unit) {
    case 'units':
      return (Math.round(value * 100) / 100).toLocaleString();
    case 'days':
      return `${Math.round(value)}d`;
    case 'percent':
      return `${value > 0 ? '+' : ''}${Math.round(value * 10) / 10}%`;
  }
}
